package com.vizio.redwolf.pay.model

import com.vizio.redwolf.pay.api.CustomerSupportApiImpl.Companion.ERROR_CONTENT_TYPE_NOT_SUPPORTED
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import io.ktor.client.call.NoTransformationFoundException
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.request
import io.ktor.http.isSuccess
import kotlin.js.JsExport

@JsExport
sealed class CustomerSupportResult<out T : Any> {

    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : CustomerSupportResult<T>()
    data class Failed(val headers: ResponseHeaders, val response: CustomerSupportErrorResponse) : CustomerSupportResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : CustomerSupportResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : CustomerSupportResult<Nothing>()

    fun <R> fold (
        onSuccess: (Success: Success<T>) -> R,
        onFailed: (Failed: Failed) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}

suspend inline fun <T : HttpResponse, reified R : Any> handleCustomerSupportHttpResult(httpResult: HttpResult<T>): CustomerSupportResult<R> {
    return when (httpResult) {
        is HttpResult.Success -> {
            val httpResponse: HttpResponse = httpResult.data
            val statusCode = httpResponse.status.value
            val headers = ResponseHeaders.Valid(statusCode)
            val requestUrl: String = httpResponse.request.url.toString()
            when {
                httpResponse.status.isSuccess() -> {
                    try {
                        CustomerSupportResult.Success(headers, httpResponse.body())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        CustomerSupportResult.Error(headers, ex)
                    }
                }
                else -> {
                    try {
                        val customerSupportErrorResponse = httpResponse.body<CustomerSupportErrorResponse>()
                        CustomerSupportResult.Failed(headers, response = customerSupportErrorResponse)
                    } catch (ex: NoTransformationFoundException) {
                        ex.printStackTrace()
                        val exception = Exception("$ERROR_CONTENT_TYPE_NOT_SUPPORTED:$requestUrl")
                        CustomerSupportResult.Error(headers, exception)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        CustomerSupportResult.Error(headers, ex)
                    }
                }
            }
        }
        is HttpResult.Error -> {
            val headers = ResponseHeaders.Invalid
            val exception = httpResult.exception
            if (exception is TimeoutException) {
                CustomerSupportResult.Timeout(headers, exception)
            } else {
                CustomerSupportResult.Error(headers, exception)
            }
        }
    }
}
